import { CompRef } from '@wix/document-services-types';
import {
  IVirtualManifest,
  IVirtualManifestMappings,
  ReducibleKey,
} from '../builders';

import { VirtualElement } from '../api/providers/VirtualElement';
import { VirtualManifestBuilder } from '../api';
import type {
  EditorPointer,
  EditorPointerServiceAPI,
} from '@wix/editor-document-logic-api';

export class BreakpointVariantNode {
  constructor(
    private editorPointerAPI: EditorPointerServiceAPI,
    private resolvedVirtualManifest: IVirtualManifest,
    private node: VirtualElement<any>,
  ) {}

  withBreakpointVariant(
    editorPointer: EditorPointer<CompRef>,
  ): IVirtualManifest {
    const nodeEditorPointer = this.node.getEditorPointer();

    if (
      !this.node.isMultipleComponents() &&
      this.editorPointerAPI.isSameEditorPointer(
        editorPointer,
        nodeEditorPointer,
      ) &&
      this.#isSameVariantContext(editorPointer, nodeEditorPointer)
    ) {
      return this.resolvedVirtualManifest;
    }

    const newVirtualManifest = { ...this.resolvedVirtualManifest };

    const updatedManifests = newVirtualManifest.manifests.map((manifest) => ({
      ...manifest,
      editorPointer: this.#updateEditorPointer(
        manifest.editorPointer,
        editorPointer,
      ),
    }));

    return {
      ...newVirtualManifest,
      virtualManifestId: VirtualManifestBuilder.isHashGenerateVirtualManifestId(
        newVirtualManifest.virtualManifestId,
      )
        ? VirtualManifestBuilder.generateHashVirtualManifestId()
        : VirtualManifestBuilder.generateVirtualManifestId(
            updatedManifests.map((x) => x.editorPointer),
            newVirtualManifest.strategy,
          ),
      manifests: updatedManifests,
      tree: {
        ...newVirtualManifest.tree,
        editorPointer: newVirtualManifest.tree.editorPointer
          ? this.#updateEditorPointer(
              newVirtualManifest.tree.editorPointer,
              editorPointer,
            )
          : null,
        mappings: this.#updateMappings(
          newVirtualManifest.tree.mappings,
          editorPointer,
        ),
        children: newVirtualManifest.tree.children.map((child) =>
          this.#processTreeChild(child, editorPointer),
        ),
      },
    };
  }

  #processTreeChild(
    child: IVirtualManifest['tree'],
    baseEditorPointer: EditorPointer<CompRef>,
  ): IVirtualManifest['tree'] {
    return {
      ...child,
      editorPointer: child.editorPointer
        ? this.#updateEditorPointer(child.editorPointer, baseEditorPointer)
        : null,
      mappings: this.#updateMappings(child.mappings, baseEditorPointer),
      children: child.children.map((grandChild) =>
        this.#processTreeChild(grandChild, baseEditorPointer),
      ),
    };
  }

  #updateEditorPointer(
    currentEditorPointer: EditorPointer<CompRef>,
    baseEditorPointer: EditorPointer<CompRef>,
  ): EditorPointer<CompRef> {
    const currentInnerElementPath =
      this.editorPointerAPI.getInnerElementPath(currentEditorPointer);

    const currentDocumentPointer =
      this.editorPointerAPI.getDocumentPointer(currentEditorPointer);
    const baseDocumentPointer =
      this.editorPointerAPI.getDocumentPointer(baseEditorPointer);

    const mergedDocumentPointer = {
      ...baseDocumentPointer,
      // id: currentDocumentPointer.id,
      type: currentDocumentPointer.type,
    };

    return this.editorPointerAPI.buildEditorPointer(
      mergedDocumentPointer,
      currentInnerElementPath,
    );
  }

  #updateMappings(
    mappings: IVirtualManifestMappings,
    baseEditorPointer: EditorPointer<CompRef>,
  ): IVirtualManifestMappings {
    if (!mappings) {
      return mappings;
    }

    const updatedMappings: IVirtualManifestMappings = {};

    for (const [reducibleKey, mappingGroups] of Object.entries(mappings)) {
      if (!mappingGroups) {
        updatedMappings[reducibleKey as ReducibleKey] = mappingGroups;
        continue;
      }

      updatedMappings[reducibleKey as ReducibleKey] = {};

      for (const [groupKey, mappingItems] of Object.entries(mappingGroups)) {
        updatedMappings[reducibleKey as ReducibleKey]![groupKey] =
          mappingItems.map((mapping) => ({
            ...mapping,
            editorPointer: this.#updateEditorPointer(
              mapping.editorPointer,
              baseEditorPointer,
            ),
          }));
      }
    }

    return updatedMappings;
  }

  #isSameVariantContext(
    editorPointerA: EditorPointer<CompRef>,
    editorPointerB: EditorPointer<CompRef>,
  ) {
    return (
      this.#getVariantContextId(editorPointerA) ===
      this.#getVariantContextId(editorPointerB)
    );
  }

  #getVariantContextId(editorPointer: EditorPointer<CompRef>) {
    return (
      this.editorPointerAPI
        .getDocumentPointer(editorPointer)
        .variants?.map((variant) => variant.id)
        .sort()
        .join(',') ?? ''
    );
  }
}
